import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { SimpleModal, BackdropLoader, SelectMenu, toast } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import { SimpleConfirmationModal } from 'src/components/shared/SimpleConfirmationModal';
import { ADD_ON_PLANS, PRODUCT_PLANS, planChangeWarning } from '../productPlans';
import useStyles from '../styles';
const NONE = '';
export const AddProductModal = observer(function AddProductModal(_ref) {
  var _store$stripe$product, _savedPlan$plan, _savedPlan$addOnNeeds, _store$stripe$createP, _store$stripe$createP2, _store$stripe$updateP, _store$stripe$updateP2, _store$stripe$setProd, _store$stripe$setProd2, _store$stripe$getProd;
  let isOpen = _ref.isOpen,
    onClose = _ref.onClose,
    onSave = _ref.onSave,
    _ref$mode = _ref.mode,
    mode = _ref$mode === void 0 ? 'add' : _ref$mode,
    product = _ref.product;
  const store = useStore();
  const classes = useStyles();
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    productName = _useState2[0],
    setProductName = _useState2[1];
  const _useState3 = useState(NONE),
    _useState4 = _slicedToArray(_useState3, 2),
    plan = _useState4[0],
    setPlan = _useState4[1];
  const _useState5 = useState(NONE),
    _useState6 = _slicedToArray(_useState5, 2),
    addOnNeedsPlan = _useState6[0],
    setAddOnNeedsPlan = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    confirmOpen = _useState8[0],
    setConfirmOpen = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    submitError = _useState0[0],
    setSubmitError = _useState0[1];
  const _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    planError = _useState10[0],
    setPlanError = _useState10[1];
  // A product created here whose plan then failed to save: saving again finishes it rather than creating another.
  const _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    createdId = _useState12[0],
    setCreatedId = _useState12[1];
  // Set while a save runs: a second click on Save or "Change plan" before the first finishes would save twice, and on a
  // new product create two.
  const saving = useRef(false);
  const isEdit = mode === 'edit';
  const showsPlans = !!store.account.featureFlagsStore.getIsBillingOptionsEnabled;
  const plansLoaded = !!store.stripe.productPlansLoaded;
  const savedPlan = isEdit && product !== null && product !== void 0 && product.id ? (_store$stripe$product = store.stripe.productPlans) === null || _store$stripe$product === void 0 ? void 0 : _store$stripe$product[product.id] : undefined;
  // An existing product's fields wait for its own row from a plan read that succeeded. Without it (no read yet, or a
  // product created since the last one) it would show None and no companies, so a change could clear its plan
  // without the confirmation.
  const hasPlanRow = plansLoaded && Boolean(savedPlan);
  const canSetPlan = showsPlans && (!isEdit || hasPlanRow);
  const planChanged = ((_savedPlan$plan = savedPlan === null || savedPlan === void 0 ? void 0 : savedPlan.plan) !== null && _savedPlan$plan !== void 0 ? _savedPlan$plan : NONE) !== plan || ((_savedPlan$addOnNeeds = savedPlan === null || savedPlan === void 0 ? void 0 : savedPlan.addOnNeedsPlan) !== null && _savedPlan$addOnNeeds !== void 0 ? _savedPlan$addOnNeeds : NONE) !== addOnNeedsPlan;
  const isSubmitting = ((_store$stripe$createP = (_store$stripe$createP2 = store.stripe.createProductAsync) === null || _store$stripe$createP2 === void 0 ? void 0 : _store$stripe$createP2.inProgress) !== null && _store$stripe$createP !== void 0 ? _store$stripe$createP : false) || ((_store$stripe$updateP = (_store$stripe$updateP2 = store.stripe.updateProductAsync) === null || _store$stripe$updateP2 === void 0 ? void 0 : _store$stripe$updateP2.inProgress) !== null && _store$stripe$updateP !== void 0 ? _store$stripe$updateP : false) || ((_store$stripe$setProd = (_store$stripe$setProd2 = store.stripe.setProductPlanAsync) === null || _store$stripe$setProd2 === void 0 ? void 0 : _store$stripe$setProd2.inProgress) !== null && _store$stripe$setProd !== void 0 ? _store$stripe$setProd : false);
  useEffect(() => {
    if (isOpen) {
      setProductName(isEdit && product ? product.name || '' : '');
      setConfirmOpen(false);
      setSubmitError(null);
      setPlanError(null);
      setCreatedId(null);
    }
  }, [isOpen, isEdit, product]);

  // Seeded when the dialog opens and when the product's row first arrives, not on every read, so a choice in
  // progress stays.
  useEffect(() => {
    if (isOpen) {
      var _savedPlan$plan2, _savedPlan$addOnNeeds2;
      setPlan((_savedPlan$plan2 = savedPlan === null || savedPlan === void 0 ? void 0 : savedPlan.plan) !== null && _savedPlan$plan2 !== void 0 ? _savedPlan$plan2 : NONE);
      setAddOnNeedsPlan((_savedPlan$addOnNeeds2 = savedPlan === null || savedPlan === void 0 ? void 0 : savedPlan.addOnNeedsPlan) !== null && _savedPlan$addOnNeeds2 !== void 0 ? _savedPlan$addOnNeeds2 : NONE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product === null || product === void 0 ? void 0 : product.id, hasPlanRow]);

  // The Pricings tab reads the plans when it opens; an existing product missing from that read asks again.
  useEffect(() => {
    if (isOpen && showsPlans && isEdit && !hasPlanRow) store.stripe.getProductPlansAsync.run();
  }, [isOpen, showsPlans, isEdit, hasPlanRow, store]);

  // While a save runs the dialog stays open: closing mid-save and reopening would let the save land on the new dialog.
  const handleClose = () => {
    if (!saving.current) onClose();
  };
  const refreshLists = () => {
    store.stripe.getPriceDashboardAsync.run();
    store.stripe.getAllProductsAsync.run();
  };
  const save = async name => {
    try {
      if (isEdit && product !== null && product !== void 0 && product.id) {
        await store.stripe.updateProductAsync.run({
          id: product.id,
          name
        });
      } else {
        await store.stripe.createProductAsync.run({
          name
        });
      }
      onSave === null || onSave === void 0 || onSave();
      onClose();
      refreshLists();
    } catch (err) {
      var _err$message;
      console.error(isEdit ? 'Update product failed:' : 'Create product failed:', err);
      setSubmitError((_err$message = err === null || err === void 0 ? void 0 : err.message) !== null && _err$message !== void 0 ? _err$message : isEdit ? 'Failed to update product' : 'Failed to create product');
    }
  };

  // With billing options on. A failed call keeps the dialog open: its toast says why, and nothing after it runs.
  const saveProductThenPlan = async name => {
    const existingId = isEdit ? product === null || product === void 0 ? void 0 : product.id : createdId;
    let productId = existingId;
    if (existingId) {
      await store.stripe.updateProductAsync.run({
        id: existingId,
        name
      });
      if (store.stripe.updateProductAsync.isError) return;
    } else {
      const created = await store.stripe.createProductAsync.run({
        name
      });
      if (store.stripe.createProductAsync.isError) return;
      // POST product answers the new id as a string (stripe-service's SuccessResponse<Guid>).
      if (typeof (created === null || created === void 0 ? void 0 : created.response) !== 'string' || !created.response) {
        // Created, but with no id its plan can't follow. Closing keeps a second Save from creating another product.
        toast('warning', {
          body: ['The product is saved. Set its plan from Edit product.']
        });
        onSave === null || onSave === void 0 || onSave();
        onClose();
        refreshLists();
        return;
      }
      productId = created.response;
      setCreatedId(productId);
    }

    // A new product is created first and then given its plan: the plan route needs its id.
    if (canSetPlan && planChanged) {
      setPlanError(null);
      await store.stripe.setProductPlanAsync.run({
        id: productId,
        plan: plan || null,
        addOnNeedsPlan: addOnNeedsPlan || null
      });
      if (store.stripe.setProductPlanAsync.isError) {
        setPlanError("The product is saved, but its plan isn't. Save again to retry.");
        refreshLists();
        return;
      }
    }
    onSave === null || onSave === void 0 || onSave();
    onClose();
    refreshLists();
    // The store already holds the saved plan; this read brings the counts and who changed it, and cancels any older read.
    store.stripe.getProductPlansAsync.run();
  };
  const saveWithPlan = async name => {
    if (saving.current) return;
    saving.current = true;
    try {
      await saveProductThenPlan(name);
    } finally {
      saving.current = false;
    }
  };
  const handleSubmit = async () => {
    const name = (productName || '').trim();
    if (!name) {
      setSubmitError('Product name is required');
      return;
    }
    setSubmitError(null);
    if (!showsPlans) {
      await save(name);
      return;
    }
    if (canSetPlan && planChangeWarning(name, savedPlan, plan)) {
      setConfirmOpen(true);
      return;
    }
    await saveWithPlan(name);
  };

  // The confirmation stays open, with its loader, until the save finishes.
  const handleConfirm = async _ref2 => {
    let action = _ref2.action;
    if (action === 'submit') await saveWithPlan((productName || '').trim());
    setConfirmOpen(false);
  };
  const planFields =
  /*#__PURE__*/
  // This theme's spacing unit is 1px, so the gaps are given in pixels.
  React.createElement(Box, {
    sx: {
      mt: '24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    "data-testid": "product-plan-field"
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    id: "product-plan",
    label: "Plan",
    value: plan,
    onChange: e => setPlan(e.target.value),
    fullWidth: true,
    displayEmpty: true,
    disabled: Boolean(addOnNeedsPlan),
    renderValue: v => v || 'None'
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: NONE
  }, "None"), PRODUCT_PLANS.map(p => /*#__PURE__*/React.createElement(MenuItem, {
    key: p,
    value: p
  }, p)))), /*#__PURE__*/React.createElement(Box, {
    "data-testid": "product-add-on-field"
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    id: "product-add-on-needs",
    label: "Add-on needs",
    value: addOnNeedsPlan,
    onChange: e => setAddOnNeedsPlan(e.target.value),
    fullWidth: true,
    displayEmpty: true,
    disabled: Boolean(plan),
    renderValue: v => v || 'None'
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: NONE
  }, "None"), ADD_ON_PLANS.map(p => /*#__PURE__*/React.createElement(MenuItem, {
    key: p,
    value: p
  }, p)))), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "A company is on the highest plan among the products it holds. An add-on says which plan a company needs to add it."), planError && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "error",
    role: "alert"
  }, planError));
  return /*#__PURE__*/React.createElement(SimpleModal, {
    trigger: null,
    isOpen: isOpen,
    handleClose: handleClose,
    title: isEdit ? 'Edit product' : 'Add product',
    leftButton: /*#__PURE__*/React.createElement(Button, {
      variant: "text",
      onClick: onClose,
      disabled: isSubmitting
    }, "Cancel"),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleSubmit,
      disabled: isSubmitting || !(productName || '').trim()
    }, isSubmitting ? 'Saving...' : 'Save product')
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isSubmitting
  }), /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    label: "Product name",
    value: productName,
    onChange: e => setProductName(e.target.value),
    error: Boolean(submitError),
    helperText: submitError,
    placeholder: "Enter product name",
    autoFocus: true,
    sx: {
      mt: 1
    }
  }), canSetPlan && planFields, showsPlans && !canSetPlan && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      mt: '24px'
    },
    "data-testid": "product-plan-unavailable"
  }, (_store$stripe$getProd = store.stripe.getProductPlansAsync) !== null && _store$stripe$getProd !== void 0 && _store$stripe$getProd.inProgress ? "Reading this product's plan..." : "This product's plan couldn't be read, so it can't be changed right now. Close and reopen to try again."), showsPlans && /*#__PURE__*/React.createElement(SimpleConfirmationModal, {
    headerText: "Change this product's plan?"
    // As a component, so the product name shows as text: bodyText is rendered as HTML.
    ,
    bodyComponent: /*#__PURE__*/React.createElement(Box, {
      className: classes.bulkDeleteConfirmBody
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body3",
      component: "div"
    }, confirmOpen ? planChangeWarning((productName || '').trim(), savedPlan, plan) : '')),
    externalIsOpen: confirmOpen,
    callBack: handleConfirm,
    customSubmitButtonName: "Change plan",
    isLoading: isSubmitting
  }));
});